import { QueryKeys, updateEntityVersionedAspectQuery } from '@common/queries/common';
import {
  Template,
  updateTemplateMutation as updateTemplateMutationQuery,
  projectTemplatesQuery
} from '@common/queries/project';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { versionedAspectPathResolver } from '@common/utils/misc/aspect';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { AspectKind } from '@prophecy/graphql-types/enums';
import { theme } from '@prophecy/theme';
import { Button, Stack, Text, DiffEditor, SlidingPanel, StackItem } from '@prophecy/ui-v3';
import { Alert, toast } from '@prophecy/ui-v3/ui-compat';
import { FROZEN_EMPTY_OBJECT } from '@prophecy/utils/constants';
import { isValidYML } from '@prophecy/utils/validation-yaml';
import { useQueryClient } from '@tanstack/react-query';
import { useGraphQlMutation, useGraphQlQuery } from 'frontend/core/src/data/util';
import { load } from 'js-yaml';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useDeleteEntity } from '../../../components/DeleteEntity';

type ProjectTemplate = {
  version: string;
};
export function CheckForTemplateUpgradeFromProjectDetail({
  projectId,
  queriedBranch
}: {
  queriedBranch: string;
  projectId: string;
}) {
  const { data } = useGraphQlQuery(
    [QueryKeys.Git, QueryKeys.Project, 'project-check-templates', { projectId }],
    projectTemplatesQuery,
    undefined,
    {
      projectId,
      branch: queriedBranch
    }
  );
  if (!data || !data.Project) {
    return null;
  }
  const { defaultTemplate, templates } = data.Project;
  return <CheckForTemplateUpgrade defaultTemplate={defaultTemplate} templates={templates} />;
}
export function CheckForTemplateUpgrade({
  defaultTemplate: defaultTemplateStr,
  templates,
  showDiff = false,
  onClose
}: {
  defaultTemplate: string;
  templates: Template[];
  showDiff?: boolean;
  onClose?: () => void;
}) {
  const currentTemplateData: Template = templates?.[0] || (FROZEN_EMPTY_OBJECT as Template);
  const currentTemplateStr = versionedAspectPathResolver(
    AspectKind.Configuration,
    currentTemplateData?.versionedAspects || [],
    'template.yml'
  ) as string;
  const {
    control,
    formState: { errors, isValid },
    handleSubmit
  } = useForm({ mode: 'onChange', defaultValues: { content: defaultTemplateStr } });
  const [showDiffDialog, setShowDiffDialog] = useState(showDiff);
  const queryClient = useQueryClient();
  const versionedAspectMutation = useGraphQlMutation(updateEntityVersionedAspectQuery);
  const updateTemplateMutation = useGraphQlMutation(updateTemplateMutationQuery);

  const id = currentTemplateData._id;
  // Restore default template by removing current aspect if user does not make any change for upgrade
  const { remove } = useDeleteEntity({
    entityId: id,
    entity: Entity.Template,
    showConfirmation: false,
    successMessage: 'Template updated successfully.'
  });

  useEffect(() => {
    if (currentTemplateStr && defaultTemplateStr && currentTemplateStr !== defaultTemplateStr) {
      const currentTemplate = load(currentTemplateStr) as ProjectTemplate;
      const defaultTemplate = load(defaultTemplateStr) as ProjectTemplate;
      if (currentTemplate.version !== defaultTemplate.version) {
        setShowDiffDialog(true);
      }
    }
  }, [currentTemplateStr, defaultTemplateStr]);

  const resetDiffDialog = async () => {
    queryClient.invalidateQueries([QueryKeys.Git, QueryKeys.Project]);
    setShowDiffDialog(false);
  };
  async function onSave({ content }: { content: string }) {
    if (content === defaultTemplateStr) {
      await remove();
      await resetDiffDialog();
      return false;
    }
    await updateTemplateMutation.mutateAsync({ uid: id });
    const aspectValue = JSON.stringify({ 'template.yml': content });
    await versionedAspectMutation.mutateAsync(
      {
        uid: id,
        entityKind: Entity.Template,
        aspectKind: 'Configuration',
        aspectValue
      },
      {
        onSuccess: async () => {
          await resetDiffDialog();
          toast.success({ content: 'Template updated successfully.' });
        }
      }
    );
  }
  const isLoading = updateTemplateMutation.isLoading || versionedAspectMutation.isLoading;
  return showDiffDialog ? (
    <SlidingPanel
      open={true}
      size='l'
      onClose={onClose}
      footer={
        <Stack direction='horizontal' align='end' alignY='center' gap={theme.spaces.x8}>
          {showDiff ? (
            <Button variant='secondaryGray' onClick={onClose} data-test-id='template-upgrade-close-button'>
              Close
            </Button>
          ) : (
            <>
              <Button
                variant='secondaryGray'
                elementType='link'
                to={Private_Routes.User.tab.getUrl({ tab: 'projects' })}
                aria-label='close-dialog'
                data-test-id='template-upgrade-cancel-button'>
                Cancel
              </Button>
              <Button
                variant='primary'
                disabled={!isValid}
                onClick={handleSubmit(onSave)}
                loading={isLoading}
                data-test-id='template-upgrade-save-button'>
                Upgrade
              </Button>
            </>
          )}
        </Stack>
      }
      closable={showDiff}>
      <Stack gap={theme.spaces.x24} height='100%' align='start'>
        {!showDiff && (
          <>
            <Text level='lg' tone={theme.colors.gray700}>
              Upgrade project build
            </Text>
            <Alert>New build version is available, please upgrade</Alert>
          </>
        )}
        <StackItem grow='1'>
          <Stack gap={theme.spaces.x10} height='100%' width='100%'>
            <Stack direction='horizontal'>
              <StackItem grow='1'>
                <Text level='sm' tone={theme.colors.gray700}>
                  Current
                </Text>
              </StackItem>
              <StackItem grow='1'>
                <Text level='sm' tone={theme.colors.gray700}>
                  New
                </Text>
              </StackItem>
            </Stack>
            <Controller
              render={({ field }) => (
                <Stack gap={theme.spaces.x8} height='100%'>
                  <DiffEditor
                    height='100%'
                    width='100%'
                    language='yaml'
                    readOnly={showDiff}
                    onModifiedChange={(value) => {
                      field.onChange(value);
                    }}
                    onModifiedBlur={field.onBlur}
                    original={currentTemplateStr}
                    modified={field.value}
                    data-test-id='template-upgrade-diff-editor'
                  />
                  {errors.content?.message ? (
                    <Alert variant='error' alignY='center'>
                      <Text level='sm13'>{renderColumnWithEllipsis(errors.content?.message)}</Text>
                    </Alert>
                  ) : null}
                </Stack>
              )}
              control={control}
              name='content'
              rules={{
                required: 'Template is required',
                validate: isValidYML
              }}
            />
          </Stack>
        </StackItem>
      </Stack>
    </SlidingPanel>
  ) : null;
}
